/**
 * CRM Multi-Compagnia · Dashboard - CrossSellingCard
 * Lista top 10 opportunità di cross-selling, con CTA "Crea attività commerciale"
 * che apre un modal preimpostato con cliente + ramo suggerito.
 */
import { useState } from "react";
import { TrendingUp, Plus, Loader2 } from "lucide-react";
import { api, Modal, RamoChip, Btn, ErrorBox, toast } from "@/components/crm-multi/shared";

export default function CrossSellingCard({ items, loading, error, onCreated }) {
  const [modal, setModal] = useState(null); // { customer_id, cliente, ramo_suggerito }
  const [titolo, setTitolo] = useState("");
  const [descrizione, setDescrizione] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState(null);

  const openCreate = (it) => {
    setModal(it);
    setTitolo(`Proposta cross-selling ${it.ramo_suggerito} a ${it.cliente}`);
    setDescrizione(it.motivo || "");
    setSubmitErr(null);
  };

  const submit = async () => {
    setSubmitting(true); setSubmitErr(null);
    try {
      await api.post("/activities", {
        customer_id: modal.customer_id,
        tipo: "task",
        titolo,
        descrizione,
        stato: "pianificata",
      });
      toast("Attività commerciale creata", "success");
      setModal(null);
      onCreated?.();
    } catch (e) {
      setSubmitErr(e?.response?.data?.detail || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4" data-testid="cm-cross-sell">
      <header className="flex items-center gap-2 mb-3">
        <TrendingUp className="w-4 h-4 text-emerald-600" />
        <h3 className="text-sm font-semibold text-zinc-900">Opportunità Cross-Selling</h3>
        <span className="text-[10px] text-zinc-500">top 10</span>
      </header>
      {loading ? (
        <p className="text-xs text-zinc-400 py-6 text-center">Caricamento...</p>
      ) : error ? (
        <ErrorBox error={error} />
      ) : !items || items.length === 0 ? (
        <p className="text-xs text-zinc-400 py-6 text-center">Nessuna opportunità rilevata</p>
      ) : (
        <ul className="divide-y divide-zinc-100 max-h-96 overflow-y-auto -mx-4 -mb-4">
          {items.slice(0, 10).map((it) => (
            <li key={it.customer_id} className="px-4 py-2.5 hover:bg-emerald-50/30" data-testid={`cm-cross-sell-row-${it.customer_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-zinc-900 truncate">{it.cliente}</p>
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    <span className="text-[10px] text-zinc-500">Suggerito:</span>
                    <RamoChip ramo={it.ramo_suggerito} size="xs" />
                  </div>
                  <p className="text-[11px] text-zinc-500 mt-1 line-clamp-2">{it.motivo}</p>
                </div>
                <button onClick={() => openCreate(it)}
                  className="px-2 py-1 text-[11px] rounded bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1 flex-shrink-0"
                  data-testid={`cm-cross-sell-cta-${it.customer_id}`}>
                  <Plus className="w-3 h-3" /> Attività
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Modal open={!!modal} onClose={() => setModal(null)} title="Crea attività commerciale" maxWidth="max-w-md" testid="cm-cross-modal">
        {modal && (
          <div className="space-y-3">
            <div className="text-xs space-y-1">
              <p><span className="text-zinc-500">Cliente:</span> <strong>{modal.cliente}</strong></p>
              <p><span className="text-zinc-500">Ramo suggerito:</span> <RamoChip ramo={modal.ramo_suggerito} size="xs" /></p>
            </div>
            <label className="block">
              <span className="text-xs font-medium text-zinc-700">Titolo</span>
              <input value={titolo} onChange={(e) => setTitolo(e.target.value)}
                className="mt-1 w-full px-3 py-1.5 text-sm border border-zinc-300 rounded"
                data-testid="cm-cross-modal-titolo" />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-zinc-700">Descrizione / motivo</span>
              <textarea rows={3} value={descrizione} onChange={(e) => setDescrizione(e.target.value)}
                className="mt-1 w-full px-3 py-1.5 text-sm border border-zinc-300 rounded"
                data-testid="cm-cross-modal-desc" />
            </label>
            <ErrorBox error={submitErr} />
            <div className="flex justify-end gap-2 pt-2">
              <Btn variant="outline" size="sm" onClick={() => setModal(null)}>Annulla</Btn>
              <Btn variant="primary" size="sm" onClick={submit} disabled={submitting || !titolo}
                data-testid="cm-cross-modal-submit">
                {submitting && <Loader2 className="w-3 h-3 animate-spin" />} Crea attività
              </Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
